import { FC, useEffect, useState, useRef, useMemo } from 'react';
import { Container, Nav, Navbar, NavbarBrand, NavbarCollapse, NavbarToggle, NavLink } from 'react-bootstrap';
import LanguageDropdown from './components/languageDropdown/LanguageDropdown.tsx';
import { useTranslation } from 'react-i18next';
import { getNavLinkWidth, getRequiredWidthForFullNavbar } from './utils/NavigationUtils.ts';
import { navigationResource } from './Navigation.i18n.ts';
import { firstName } from '../../constants/personalInfo/personalInfo.ts';
import styles from './Navigation.module.scss';

const Navigation: FC = () => {
    const { 
        i18n
    } = useTranslation('navigation');
    
    const [isCollapsed, setIsCollapsed] = useState<boolean>(true); // ALWAYS set it to TRUE initially, so we can measure the required width correctly
    const [requiredWidthForFullNavbar, setRequiredWidthForFullNavbar] = useState(0);

    const navbarBrandRef = useRef<HTMLAnchorElement | null>(null);
    const navbarNavRef = useRef<HTMLDivElement | null>(null);
    const languageDropdownSelectRef = useRef<HTMLSelectElement | null>(null);

    const navLinks = navigationResource[i18n.language as keyof typeof navigationResource].navLinks;
    const navLinksKeys: string[] = Object.keys(navLinks);
    const navLinksAmount: number = navLinksKeys.length;

    const navLinkWidth: number = useMemo(() => {
        const navLinkWidth: number = getNavLinkWidth();
        return navLinkWidth;
    }, []);

    useEffect(() => {
        const navbarBrandElement: HTMLAnchorElement | null = navbarBrandRef.current;
        if (!navbarBrandElement) {
            return;
        }

        const navbarNavElement: HTMLDivElement | null = navbarNavRef.current;
        if (!navbarNavElement) {
            return;
        }

        const languageDropdownSelectElement: HTMLSelectElement | null = languageDropdownSelectRef.current;
        if (!languageDropdownSelectElement) {
            return;
        }

        const width: number = getRequiredWidthForFullNavbar({
            navLinksAmount,
            navLinkWidth,
            navbarBrandElement,
            navbarNavElement,
            languageDropdownSelectElement
        });
        setRequiredWidthForFullNavbar(width);
    }, [navLinksAmount, navLinkWidth]);

    useEffect(() => {
        /**
         * Handles the resize event to determine if the navbar should be collapsed.
         * @returns {void}
         */
        const handleResize = (): void => {
            const currentWidth: number = window.innerWidth;
            if (
                isCollapsed && 
                currentWidth >= requiredWidthForFullNavbar
            ) {
                setIsCollapsed(false);
                return;
            }
            if (
                !isCollapsed && 
                currentWidth < requiredWidthForFullNavbar
            ) {
                setIsCollapsed(true);
            }
        };

        // Initial check on mount
        handleResize();

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isCollapsed, requiredWidthForFullNavbar]);

    return (
        <Navbar
            expand={!isCollapsed}
            collapseOnSelect
            className={styles.navbar}
        >
            <Container
                fluid
                className="d-flex align-items-center"
                style={{
                    padding: `0 var(--navbar-padding-horizontal)`
                }}
            >
                <NavbarBrand
                    ref={navbarBrandRef}
                    className={styles.brand}
                >
                    {firstName}
                </NavbarBrand>
                {/* When the navbar IS NOT expanded, we place the language dropdown at the LEFT side of the hamburger */}
                {isCollapsed && (
                    <div
                        className={styles.languageDropdownContainerWhenNotCollapsed}
                    >
                        <LanguageDropdown
                            selectRef={languageDropdownSelectRef}
                            tabIndex={0}
                        />
                    </div>
                )}
                <NavbarToggle
                    aria-controls="navbar-nav"
                    className={styles.navbarToggle}
                    tabIndex={1}
                />
                <NavbarCollapse
                    id="navbar-nav"
                    className={`w-100 order-md-1 ${isCollapsed ? styles.navbarCollapseShow : ''}`}
                >
                    <Nav
                        className={`justify-content-end w-100 ${styles.navLinks} ${isCollapsed ? 'flex-column' : ''}`}
                        ref={navbarNavRef}
                    >
                        {navLinksKeys.map((navLinkKey, index) => (
                            <NavLink
                                key={navLinkKey}
                                href={`#${navLinkKey}`}
                                className={styles.navLink}
                                style={{ 
                                    width: `${navLinkWidth}px`
                                }}
                                tabIndex={isCollapsed ? index + 1 : index}
                            >
                                {navLinks[navLinkKey as keyof typeof navLinks]}
                            </NavLink>
                        ))}
                        {/* When the navbar IS expanded, we place the language dropdown at the RIGHT side of the hamburger */}
                        {!isCollapsed && (
                            <div>
                                <LanguageDropdown
                                    selectRef={languageDropdownSelectRef}
                                    tabIndex={navLinksAmount}
                                />
                            </div>
                        )}
                    </Nav>
                </NavbarCollapse>
            </Container>
        </Navbar>
    );
};

export default Navigation;